import type { InRateZeroableReason } from '../coachCommissionTable/types';

/**
 * 「노쇼 수업은 항상 IN」 설정 화면의 달 계산·문구. 금액은 다루지 않는다(서버가 계산한다).
 * 달은 서버와 같은 'YYYY-MM'(두 자리 월) 문자열이다 — 문자열 비교가 곧 달 비교다.
 */

export type YearMonth = { year: number; month: number };

const YEAR_MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

export const toYearMonthString = ({ year, month }: YearMonth): string => `${year}-${String(month).padStart(2, '0')}`;

export const parseYearMonth = (value: unknown): YearMonth | null => {
    if (typeof value !== 'string') return null;
    const matched = YEAR_MONTH_PATTERN.exec(value);
    if (!matched) return null;
    return { year: Number(matched[1]), month: Number(matched[2]) };
};

export const addMonths = (value: YearMonth, delta: number): YearMonth => {
    const index = value.year * 12 + (value.month - 1) + delta;
    return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};

const compare = (a: YearMonth, b: YearMonth) => a.year * 12 + a.month - (b.year * 12 + b.month);

/** 이번 해면 「11월」, 다른 해면 「2025년 11월」. */
export const formatYearMonth = (value: YearMonth, now: YearMonth): string =>
    value.year === now.year ? `${value.month}월` : `${value.year}년 ${value.month}월`;

const formatRange = (from: YearMonth, to: YearMonth, now: YearMonth): string =>
    compare(from, to) === 0 ? formatYearMonth(from, now) : `${formatYearMonth(from, now)}~${formatYearMonth(to, now)}`;

/** 이 달(year·month) 수업에 설정이 걸리는가. 시작 달이 없거나 형식이 틀리면 꺼진 것으로 본다. */
export const isNoshowAlwaysInActiveFor = (fromMonth: string | null | undefined, year: string | number, month: string | number) => {
    const from = parseYearMonth(fromMonth);
    const y = Number(year);
    const m = Number(month);
    if (!from || !Number.isInteger(y) || !Number.isInteger(m)) return false;
    return compare({ year: y, month: m }, from) >= 0;
};

/** 노쇼 설정만 풀린다 — 예외 수업료의 「항상 IN 요율의 배수」는 그 설정과 따로 계속 적용된다. */
const KEEP_IN_MULTIPLIER_NOTE = '예외 수업료의 「항상 IN 요율의 배수」는 그대로 적용돼요.';

/**
 * 저장 전 확인 문구. before·after 는 시작 달('YYYY-MM') 또는 null(꺼짐). 같으면 null(바뀌는 것 없음).
 * 사용자 결정(2026-11-03): 과거 달을 고르면 지난 급여가 바뀐다는 확인, 끄면 켜져 있던 모든 달이 원래대로 돌아간다는 확인.
 */
export const describeNoshowAlwaysInChange = (
    before: string | null,
    after: string | null,
    now: YearMonth,
): { title: string; lines: string[] } | null => {
    const from = parseYearMonth(before);
    const to = parseYearMonth(after);
    const lastMonth = addMonths(now, -1);
    if (!to) {
        if (!from) return null;
        return {
            title: '노쇼 IN 계산을 끌까요?',
            lines: [
                `${formatYearMonth(from, now)}부터 노쇼 수업이 모두 근무 시간 기준 IN/OUT 으로 돌아가요.`,
                KEEP_IN_MULTIPLIER_NOTE,
            ],
        };
    }
    if (!from) {
        const lines = [`${formatYearMonth(to, now)} 수업부터 노쇼는 근무 시간과 상관없이 IN 요율로 계산해요.`];
        if (compare(to, now) < 0) lines.push(`이미 계산한 ${formatRange(to, lastMonth, now)} 급여도 다시 계산돼요.`);
        return { title: '노쇼 수업을 항상 IN 으로 계산할까요?', lines };
    }
    const diff = compare(to, from);
    if (diff === 0) return null;
    const lines =
        diff < 0
            ? [`${formatRange(to, addMonths(from, -1), now)} 급여도 노쇼를 IN 으로 다시 계산해요.`]
            : [
                  `${formatRange(from, addMonths(to, -1), now)} 노쇼 수업은 근무 시간 기준 IN/OUT 으로 돌아가요.`,
                  KEEP_IN_MULTIPLIER_NOTE,
              ];
    return { title: `${formatYearMonth(to, now)}부터 적용할까요?`, lines };
};

/** 0원 위험 사유 — 짧게(확인 창 강사 이름 옆). */
export const zeroableReasonLabel = (reason: InRateZeroableReason | string | undefined): string => {
    if (reason === 'empty') return 'IN 구간 없음';
    if (reason === 'startsAboveZero') return '첫 구간이 0만원부터가 아님';
    if (reason === 'zeroRateBracket') return '0인 구간 있음';
    return 'IN 요율 확인 필요';
};
